<!--
PlatformWebhooksModal — create/edit a subscription of the platform events
console (/config/webhooks). Registered centrally in ModalManager.vue, opened via
eventBus 'modal.platformWebhooks.open', optionally with { row } for edit.

Owns the network (saveSubscription through usePlatformWebhooks) and the one
thing the page must never lose: a secret the platform MINTED (blank secret on
a webhook subscription) is shown here ONCE, after the save, and nowhere else —
the console never reads secrets back. The modal therefore stays open after a
save that minted one, and closes by itself otherwise.
-->
<template>
  <modal
    elem-id="modal_platform_webhooks"
    :title="isManaged ? t('Assinatura gerida pela stack') : isEdit ? t('Editar assinatura') : t('Nova assinatura')"
    :show="isVisible"
    size="medium"
    :close-label="mintedSecret ? t('Fechar') : t('Cancelar')"
    :save-label="t('Salvar')"
    :save-fn="handleSave"
    :save-disabled-fn="() => isSaving || isManaged || mintedSecret !== null"
    save-test-id="platform-webhooks-modal-save"
    @close="closeModal"
    @dismiss="closeModal"
  >
    <template v-if="isVisible">
      <p v-if="isManaged" class="platform-webhooks-modal-intro" data-testid="platform-webhooks-modal-managed">
        {{ t('Esta assinatura é declarada pelo próprio serviço (events.yml) e a stack a mantém: destino, eventos e segredo vêm de lá e voltam a cada boot. Aqui ela é só leitura — para desligá-la use o botão Ativa na lista.') }}
      </p>
      <p v-else class="platform-webhooks-modal-intro" data-testid="platform-webhooks-modal-intro">
        {{ t('Um serviço recebe os eventos da plataforma por POST assinado com HMAC (remoto) ou pelo broker da stack (nats). Segredo vazio numa assinatura webhook = a plataforma cunha um e mostra uma única vez.') }}
      </p>

      <platform-webhooks-form
        ref="formRef"
        v-model="form"
        :event-names="eventNames"
        :is-edit="isEdit"
        :read-only="isManaged"
      />

      <div v-if="mintedSecret" class="platform-webhooks-modal-secret" data-testid="platform-webhooks-modal-secret">
        <strong>{{ t('Segredo cunhado — mostrado uma única vez, guarde no env do serviço:') }}</strong>
        <code data-testid="platform-webhooks-modal-secret-value">{{ mintedSecret }}</code>
      </div>
    </template>
  </modal>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { eventBus } from '@/eventBus'
import { log } from '@/utils/DebugLogger'
import { usePlatformWebhooks } from '@/composables/usePlatformWebhooks'
import PlatformWebhooksForm from './PlatformWebhooksForm.vue'
import type { PlatformWebhooks_SubscriptionRow } from '@/contracts/PlatformWebhooks_SubscriptionRow'
import type { PlatformWebhooks_SubscriptionSaveInput } from '@/contracts/PlatformWebhooks_SubscriptionSaveInput'

const { t } = useI18n()
const { catalog, fetchCatalog, saveSubscription, lastError } = usePlatformWebhooks()

const isVisible = ref(false)
const isSaving = ref(false)
const editingId = ref<number | null>(null)
const mintedSecret = ref<string | null>(null)
const formRef = ref<{ validateForm: () => boolean } | null>(null)

const blank = (): PlatformWebhooks_SubscriptionSaveInput => ({ plugin: '', url: '', events: [], secret: '', enabled: true, contract: '2.0', delivery: 'webhook' })
const form = ref<PlatformWebhooks_SubscriptionSaveInput>(blank())

const isEdit = computed(() => editingId.value !== null)
const isManaged = ref(false)
const eventNames = computed(() => catalog.value.map((e) => e.name ?? ''))

async function openModal(payload?: { row: PlatformWebhooks_SubscriptionRow }): Promise<void> {
  log('PlatformWebhooksModal', 'openModal', 'Abrindo modal de assinatura', { edit: !!payload?.row })
  mintedSecret.value = null
  editingId.value = null
  isManaged.value = payload?.row?.managed === true
  form.value = blank()
  const row = payload?.row
  if (row) {
    editingId.value = row.id
    form.value = {
      plugin: row.plugin ?? '',
      url: row.url ?? '',
      events: [...(row.events ?? [])],
      secret: '',
      enabled: row.enabled ?? true,
      contract: row.contract ?? '2.0',
      delivery: row.delivery ?? 'webhook',
    }
  }
  isVisible.value = true
  if (catalog.value.length === 0) await fetchCatalog()
}

function closeModal(): void {
  isVisible.value = false
  mintedSecret.value = null
  editingId.value = null
  isManaged.value = false
  form.value = blank()
}

async function handleSave(): Promise<void> {
  if (isManaged.value) return
  if (!formRef.value?.validateForm()) return
  isSaving.value = true
  try {
    const v = form.value
    const r = await saveSubscription({
      // Optional on the generated contract; validateForm() above has already
      // rejected a missing plugin/url, and secret is legitimately absent.
      plugin: (v.plugin ?? '').trim(),
      url: v.delivery === 'nats' ? '' : (v.url ?? '').trim(),
      events: v.events,
      secret: (v.secret ?? '').trim(),
      enabled: v.enabled,
      contract: v.contract,
      delivery: v.delivery,
    })
    if (!r) {
      eventBus.emit('globalMessage.error', t('Erro ao salvar a assinatura') + ': ' + (lastError.value ?? ''))
      return
    }
    log('PlatformWebhooksModal', 'handleSave', 'Assinatura salva', { id: r.row?.id, created: r.created })
    eventBus.emit('globalMessage.success', r.created ? t('Assinatura criada') : t('Assinatura atualizada'))
    eventBus.emit('platformWebhooks.saved', { row: r.row, created: r.created })
    if (r.secret) {
      mintedSecret.value = r.secret
      return
    }
    closeModal()
  } finally {
    isSaving.value = false
  }
}

onMounted(() => {
  eventBus.on('modal.platformWebhooks.open', openModal)
})

onBeforeUnmount(() => {
  eventBus.off('modal.platformWebhooks.open', openModal)
})
</script>

<style scoped>
.platform-webhooks-modal-intro {
  margin: 0 0 1rem 0;
  font-size: 0.85rem;
  color: var(--p-text-muted-color, #6b7280);
}
.platform-webhooks-modal-secret {
  margin-top: 1rem;
  padding: 0.75rem;
  border: 1px solid var(--p-content-border-color, #e5e7eb);
  border-radius: 6px;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  font-size: 0.85rem;
}
.platform-webhooks-modal-secret code {
  overflow-wrap: anywhere;
}
</style>
